<template>
<div class="wiki-edit-page">
    <CommonOverlay :show="isUploadingImage" title="Uploading Image" message="Please wait while the image is uploaded..." />
    <!-- Header with breadcrumb and actions -->
    <div class="wiki-edit-header">
      <div class="header-left">
        <router-link :to="{ path: `/app/wiki/`}" class="back-link">
          <i class="fa fa-arrow-left"></i> Back to Wiki
        </router-link>
        <h1 class="page-title" v-if="wikiPage && wikiPage.length > 0 && wikiPage[0] && wikiPage[0][0]">
          Editing: {{ wikiPage[0][0].Name }}
        </h1>
        <h1 class="page-title" v-else>
          Edit Wiki Page
        </h1>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-success save-btn"
          @click="triggerSave"
          :disabled="show"
        >
          <i class="fa" :class="show ? 'fa-spinner fa-spin' : 'fa-save'"></i> Save
        </button>
        <router-link
          :to="{ name: 'WikiView', params: { wikiPageID: `${this.wikiPageID}` } }"
          class="btn btn-outline-secondary view-btn"
        >
          <i class="fa fa-eye"></i> View Page
        </router-link>
        <button type="button" class="btn btn-outline-primary" @click="gotoCreatePage()">
          <i class="fa fa-plus"></i> New Page
        </button>
      </div>
    </div>

    <!-- Main content wrapper -->
    <div class="wiki-edit-content-wrapper">
      <div class="wiki-edit-main">
        <!--
          NOTE: Save button is now in the header for better UX
          The CustomEditor's internal save button is hidden via hideSaveButton prop
        -->

              <!-- Wiki Name Row - Full width, required -->
              <div class="wiki-name-row mt-3 mb-2">
                <div class="settings-panel wiki-name-panel" :class="{ 'wiki-name-error': wikiNameError }">
                  <div class="panel-header">
                    <span class="panel-icon">📝</span>
                    <span class="panel-title">Wiki Name <span class="required-star">*</span></span>
                  </div>
                  <div class="panel-content">
                    <b-form-input
                      v-model="wikiName"
                      placeholder="Enter wiki page name..."
                      size="sm"
                      class="wiki-name-input"
                      :class="{ 'is-invalid': wikiNameError }"
                      @input="wikiNameError = false"
                    ></b-form-input>
                    <small v-if="wikiNameError" class="text-danger mt-1 d-block">
                      Wiki name is required.
                    </small>
                  </div>
                </div>
              </div>

              <!-- Settings Row - Side by Side Panels -->
              <div class="settings-row mb-3">
                <!-- Tags Panel -->
                <div class="settings-panel tags-panel">
                  <div class="panel-header">
                    <span class="panel-icon">🏷️</span>
                    <span class="panel-title">Tags</span>
                  </div>
                  <div class="panel-content tags-content">
                    <multiselect
                      v-if="teamioTags"
                      v-model="selectedTags"
                      :options="teamioTags"
                      track-by="Tag"
                      :multiple="true"
                      :taggable="true"
                      tag-placeholder="Add this as new tag"
                      placeholder="Search or add tags"
                      @tag="addTag"
                      label="Tag"
                      :close-on-select="false"
                      :clear-on-select="false"
                      :preserve-search="false"
                      :preselect-first="false"
                      :hide-selected="true"
                    ></multiselect>
                  </div>
                </div>

                <!-- Page Settings Panel -->
                <div class="settings-panel page-settings-panel">
                  <div class="panel-header">
                    <span class="panel-icon">⚙️</span>
                    <span class="panel-title">Page Settings</span>
                  </div>
                  <div class="panel-content">
                    <b-form-group class="abc-checkbox compact-checkbox mb-1">
                      <input type="checkbox" id="mark-as-email-template" class="custom-control-input" v-model="isEmailTemplate" @click="updateTemplateTag('email')" />
                      <label for="mark-as-email-template"></label>Email Template
                    </b-form-group>
                    <b-form-group class="abc-checkbox compact-checkbox mb-0">
                      <input type="checkbox" id="mark-as-sms-template" class="custom-control-input" v-model="isSmsTemplate" @click="updateTemplateTag('sms')" />
                      <label for="mark-as-sms-template"></label>SMS Template
                    </b-form-group>
                  </div>
                </div>

                <!-- Email Subject Panel - Only show for Email templates -->
                <div v-if="isEmailTemplate" class="settings-panel email-subject-panel" :class="{ 'email-subject-error': emailSubjectError }">
                  <div class="panel-header">
                    <span class="panel-icon">✉️</span>
                    <span class="panel-title">Email Subject <span class="required-star">*</span></span>
                  </div>
                  <div class="panel-content">
                    <b-form-input
                      v-model="emailSubject"
                      placeholder="Enter email subject line..."
                      size="sm"
                      class="email-subject-input"
                      :class="{ 'is-invalid': emailSubjectError }"
                      @input="emailSubjectError = false"
                    ></b-form-input>
                    <small v-if="emailSubjectError" class="text-danger mt-1 d-block">
                      Email subject is required for email templates.
                    </small>
                  </div>
                </div>

                <!-- Public Access Panel -->
                <div class="settings-panel public-access-panel">
                  <div class="panel-header">
                    <span class="panel-icon">🔗</span>
                    <span class="panel-title">Public Access</span>
                  </div>
                  <div class="panel-content">
                    <b-form-group class="abc-checkbox compact-checkbox mb-1">
                      <input type="checkbox" id="mark-as-public" class="custom-control-input" v-model="isPublic" />
                      <label for="mark-as-public"></label>Make publicly accessible
                    </b-form-group>

                  </div>
                </div>

                <!-- Translations Panel - Only show for SMS/Email templates -->
                <div v-if="isSmsTemplate || isEmailTemplate" class="settings-panel translation-panel">
                  <div class="panel-header">
                    <span class="panel-icon">🌐</span>
                    <span class="panel-title">Translations</span>
                  </div>
                  <div class="panel-content">
                    <div v-for="lang in translationLanguages" :key="lang.code" class="translation-lang-row">
                      <input type="checkbox"
                             :id="'lang-' + lang.code"
                             v-model="selectedTranslationLanguages"
                             :value="lang.code"
                             class="translation-checkbox" />
                      <label :for="'lang-' + lang.code" class="translation-label">
                        <span class="lang-flag">{{ lang.flag }}</span>
                        <span class="lang-name">{{ lang.name }}</span>
                      </label>
                      <span v-if="translationStatuses[lang.code]" class="status-badge status-ready">
                        <i class="fa fa-check-circle text-success"></i>
                      </span>
                    </div>
                    <p class="auto-translate-info mb-0 mt-2">
                      <small class="text-muted">Selected languages auto-translated on save</small>
                    </p>
                  </div>
                </div>



              </div>

              <!-- Page Content Section -->
              <div class="content-section mt-3">
                <div class="content-section-header">
                  <i class="fa fa-pencil-square-o content-section-icon"></i>
                  <span class="content-section-title">Page Content</span>
                  <span class="content-section-hint">Edit your page content below</span>
                  <b-dropdown variant="outline-secondary" size="sm" class="ml-2 insert-dropdown" boundary="viewport" no-caret>
                    <template slot="button-content">
                      <strong>+ Insert</strong> <i class="fa fa-caret-down ml-1"></i>
                    </template>
                    <div class="insert-dropdown-content">
                      <h6 class="insert-section-title">Variables</h6>
                      <div class="variable-grid px-2 pb-2">
                        <span v-for="v in availableVariables" :key="v.value"
                          class="variable-chip" :title="v.value" @click="insertVariable(v.value)">{{ v.label || v.value }}</span>
                      </div>
                      <template v-if="isReviewRequestTemplate">
                        <h6 class="insert-section-title border-top pt-2 mt-1">Reviews</h6>
                        <div class="variable-grid px-2 pb-2">
                          <span v-for="v in reviewVariables" :key="v.value"
                            class="variable-chip" :title="v.value" @click="insertVariable(v.value)">{{ v.label || v.value }}</span>
                        </div>
                      </template>
                      <h6 class="insert-section-title border-top pt-2 mt-1">Emojis</h6>
                      <div class="emoji-inline-grid px-2 pb-2">
                        <div v-for="(emojiGroup, category) in emojiTable" :key="category">
                          <h6 class="emoji-category-title">{{ category }}</h6>
                          <div class="emoji-grid">
                            <span v-for="(emoji, emojiName) in emojiGroup" :key="emojiName"
                              class="emoji-item" :title="emojiName"
                              @click="insertEmoji(emoji)">{{ emoji }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-dropdown>
                </div>
                <div class="content-editor-container">
                  <b-overlay :show="show" rounded="sm">
                    <CustomEditor
                        ref="customEditor"
                        v-if="this.wikiPage && this.wikiPages && this.wikiPages.length > 0"
                        :editorData="this.editorData"
                        :editorTools="editorTools"
                        :hideSaveButton="true"
                        @save="save"
                        @onReady="onReady"
                        @onChange="onChange"
                        save-button-id="save-button"
                        />
                  </b-overlay>
                </div>
              </div>

              <!-- SMS status card: the single counter + warn/block guidance, in a clean readable
                   card (never on the preview panel's gradient). Shown for SMS templates whenever
                   there's content; template-aware ({{merge_tags}} rendered with sample values). The
                   save-gate enforces the hard block. (Mirrors WikiCreate.) -->
              <div v-if="isSmsTemplate && smsPlainText && smsPlainText.trim()" class="sms-status-card mt-4">
                <SmsGuidance :text="smsPlainText || ''" substitute-tags :show-balance="false" />
              </div>

              <!-- Inline SMS Preview Section — only for SMS templates AND only when the message is
                   clean (severity 'ok'). A long / over-limit message hides the phone mockup so the
                   status card above is the single, unambiguous signal — a polished preview of an
                   undeliverable text would mislead (chosen UX, 2026-07-10). -->
              <div v-if="isSmsTemplate && smsPreviewSeverity === 'ok'" class="sms-preview-section mt-4">
                <div class="preview-header" @click="togglePreviewSection">
                  <div class="preview-header-content">
                    <div class="preview-title">
                      <i class="fa" :class="showInlinePreview ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      <span class="title-icon">📱</span>
                      <span class="title-text">SMS Preview</span>
                    </div>
                    <div class="preview-status">
                      <span class="status-badge status-ready">
                        <i class="fa fa-check"></i> {{ Object.keys(translationStatuses).length }} translations
                      </span>
                    </div>
                  </div>
                </div>

                <b-collapse v-model="showInlinePreview">
                  <!-- Loading State -->
                  <div v-if="translationLoading" class="preview-loading">
                    <div class="loading-content">
                      <i class="fa fa-spinner fa-spin fa-2x"></i>
                      <p>Generating translation...</p>
                    </div>
                  </div>

                  <!-- Phone Previews -->
                  <div v-else class="inline-phone-preview" :class="{'single-phone': activeTranslationPreviews.length === 0}">
                    <!-- English Phone -->
                    <div class="phone-mockup-inline">
                      <div class="phone-frame-inline">
                        <div class="phone-notch-inline"></div>
                        <div class="phone-screen-inline">
                          <div class="phone-header-inline">
                            <span class="phone-time-inline">{{ currentTime }}</span>
                            <span class="phone-carrier-inline">TeamIO</span>
                          </div>
                          <div class="phone-content-inline">
                            <!-- Show split messages if applicable -->
                            <template v-if="smsSplitPreview && smsSplitPreview.length > 1">
                              <div
                                v-for="(segment, idx) in smsSplitPreview"
                                :key="'seg-'+idx"
                                class="message-bubble-inline outgoing"
                                :class="{'split-bubble': true}"
                              >
                                <div class="split-indicator">{{ idx + 1 }}/{{ smsSplitPreview.length }}</div>
                                <div class="message-text-inline">{{ segment }}</div>
                                <div class="message-time-inline">{{ idx === 0 ? 'Just now' : '' }}</div>
                              </div>
                            </template>
                            <!-- Single message -->
                            <template v-else>
                              <div class="message-bubble-inline outgoing">
                                <div class="message-text-inline" v-html="englishSmsPreview"></div>
                                <div class="message-time-inline">Just now</div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div class="phone-label-inline">🇺🇸 English</div>
                    </div>

                    <!-- Translation Phones - One per language with content -->
                    <div v-for="lang in activeTranslationPreviews" :key="lang.code" class="phone-mockup-inline">
                      <div class="phone-frame-inline">
                        <div class="phone-notch-inline"></div>
                        <div class="phone-screen-inline">
                          <div class="phone-header-inline">
                            <span class="phone-time-inline">{{ currentTime }}</span>
                            <span class="phone-carrier-inline">TeamIO</span>
                          </div>
                          <div class="phone-content-inline">
                            <div class="message-bubble-inline outgoing">
                              <div class="message-text-inline" v-html="lang.previewContent || 'Translation will appear after save'"></div>
                              <div class="message-time-inline">{{ lang.timeLabel }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="phone-label-inline">{{ lang.flag }} {{ lang.name }}</div>
                    </div>
                  </div>

                  <!-- Action Buttons per language -->
                  <div class="preview-actions" v-if="activeTranslationPreviews.length > 0">
                    <template v-for="lang in activeTranslationPreviews">
                      <button
                        :key="'edit-'+lang.code"
                        type="button"
                        class="btn-action btn-edit"
                        @click="showTranslationEditor(lang.code)"
                      >
                        <i class="fa fa-edit"></i> Edit {{ lang.name }}
                      </button>
                      <button
                        :key="'retrans-'+lang.code"
                        type="button"
                        class="btn-action btn-refresh"
                        @click="retranslateLanguage(lang.code)"
                        :disabled="translationLoading"
                      >
                        <i class="fa fa-refresh" :class="{'fa-spin': translationLoading}"></i> Re-translate {{ lang.name }}
                      </button>
                    </template>
                  </div>
                  
                  <!-- Info Note -->
                  <div class="preview-footer">
                    <i class="fa fa-info-circle"></i>
                    Variables like <code v-pre>{{patient_first_name}}</code> will be replaced with actual patient data when sent.
                  </div>
                </b-collapse>
              </div>

              <!-- Inline Email Preview Section - Only for Email templates -->
              <div v-if="isEmailTemplate" class="email-preview-section mt-4">
                <div class="email-preview-header" @click="showEmailPreview = !showEmailPreview">
                  <div class="preview-header-content">
                    <div class="preview-title">
                      <i class="fa" :class="showEmailPreview ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      <span class="title-icon">📧</span>
                      <span class="title-text">Email Preview</span>
                    </div>
                    <div class="preview-status">
                      <span class="status-badge status-email">
                        <i class="fa fa-envelope"></i> Email Template
                      </span>
                    </div>
                  </div>
                </div>
                
                <b-collapse v-model="showEmailPreview">
                  <div class="email-mockup">
                    <!-- Email Client Header -->
                    <div class="email-client-header">
                      <div class="email-dots">
                        <span class="dot red"></span>
                        <span class="dot yellow"></span>
                        <span class="dot green"></span>
                      </div>
                      <div class="email-tab">
                        <i class="fa fa-envelope-o"></i> New Message
                      </div>
                    </div>
                    
                    <!-- Email Content -->
                    <div class="email-content">
                      <div class="email-field">
                        <span class="field-label">From:</span>
                        <span class="field-value">{{ siteName }} &lt;{{ siteVerifiedEmail }}&gt;</span>
                      </div>
                      <div class="email-field">
                        <span class="field-label">To:</span>
                        <span class="field-value text-muted">John Smith &lt;patient@example.com&gt;</span>
                      </div>
                      <div class="email-field">
                        <span class="field-label">Subject:</span>
                        <span class="field-value subject" v-html="emailSubjectPreview"></span>
                      </div>
                      <div class="email-divider"></div>
                      <div class="email-body" v-html="emailBodyPreview"></div>
                    </div>
                  </div>
                  
                  <div class="email-preview-footer">
                    <i class="fa fa-info-circle"></i>
                    Variables will be replaced with actual patient data. Email styling may vary by email client.
                  </div>
                </b-collapse>
              </div>

      </div>

      <!-- Sidebar with browse by tag -->
      <div class="wiki-edit-sidebar" v-if="siteTags && siteTags.length > 0">
        <div class="sidebar-section">
          <h4 class="sidebar-title">Browse by Tag</h4>
          <div class="sidebar-tags">
            <router-link
              v-for="(tag, index) in siteTags"
              :key="index"
              :to="{ path: `/app/wiki/${tag.Tag}` }"
              class="sidebar-tag"
            >
              {{ tag.Tag }}
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Translation Edit Modal - Language agnostic -->
    <b-modal
      ref="translationEditModal"
      id="translation-edit-modal"
      :title="editingTranslationLang ? '✏️ Edit ' + editingTranslationLang.name + ' Translation' : '✏️ Edit Translation'"
      size="xl"
      hide-footer
      body-class="p-0"
    >
      <div class="spanish-editor-wrapper">
        <!-- Loading State -->
        <div v-if="translationEditorLoading" class="text-center py-5">
          <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
          <p class="mt-3 text-muted">Loading translations...</p>
        </div>

        <!-- Editor Content -->
        <div v-else class="spanish-editor-content">
          <!-- Side by Side Panels -->
          <div class="editor-panels">
            <!-- English Panel (Read-only) -->
            <div class="editor-panel english-panel">
              <div class="panel-header">
                <span class="flag-icon">🇺🇸</span>
                <span class="panel-title">English (Original)</span>
                <span class="badge badge-secondary">Read-only</span>
              </div>
              <div class="panel-body">
                <div v-if="englishPreviewBlocks.length === 0" class="text-center text-muted py-4">
                  <i class="fa fa-file-text-o fa-2x mb-2"></i>
                  <p>No content found</p>
                </div>
                <div v-else>
                  <div v-for="(block, index) in englishPreviewBlocks" :key="'en-'+index" class="content-block">
                    <div v-if="block.type === 'header'" class="block-header" :style="block.data.alignment ? { textAlign: block.data.alignment } : null" v-html="block.data.text"></div>
                    <div v-else-if="block.type === 'paragraph'" class="block-paragraph" :style="block.data.alignment ? { textAlign: block.data.alignment } : null" v-html="block.data.text"></div>
                    <ul v-else-if="block.type === 'list' && block.data.style === 'unordered'" class="block-list">
                      <li v-for="(item, i) in block.data.items" :key="i" v-html="item"></li>
                    </ul>
                    <ol v-else-if="block.type === 'list' && block.data.style === 'ordered'" class="block-list">
                      <li v-for="(item, i) in block.data.items" :key="i" v-html="item"></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Translation Panel (Editable) -->
            <div class="editor-panel spanish-panel">
              <div class="panel-header">
                <span class="flag-icon">{{ editingTranslationLang ? editingTranslationLang.flag : '' }}</span>
                <span class="panel-title">{{ editingTranslationLang ? editingTranslationLang.name : '' }} (Editable)</span>
                <span class="badge badge-success">Edit below</span>
              </div>
              <div class="panel-body">
                <div v-if="translationEditBlocks.length === 0" class="text-center text-muted py-4">
                  <i class="fa fa-language fa-2x mb-2"></i>
                  <p>No translation found</p>
                </div>
                <div v-else>
                  <div v-for="(block, index) in translationEditBlocks" :key="'tr-'+index" class="edit-block">
                    <div class="block-label">
                      <i :class="getBlockIcon(block.type)"></i>
                      {{ getBlockTypeLabel(block.type) }}
                    </div>
                    <!-- Header or Paragraph -->
                    <textarea
                      v-if="block.type === 'paragraph' || block.type === 'header'"
                      v-model="block.data.text"
                      class="edit-textarea"
                      :class="{'header-input': block.type === 'header'}"
                      :rows="block.type === 'header' ? 2 : 4"
                      :placeholder="'Enter ' + (editingTranslationLang ? editingTranslationLang.name : '') + ' ' + block.type + '...'"
                    ></textarea>
                    <!-- List Items -->
                    <div v-else-if="block.type === 'list'" class="list-editor">
                      <div v-for="(item, i) in block.data.items" :key="i" class="list-item-edit">
                        <span class="list-number">{{ i + 1 }}.</span>
                        <input
                          type="text"
                          v-model="block.data.items[i]"
                          class="edit-input"
                          placeholder="Enter list item..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer Info & Actions -->
          <div class="editor-footer">
            <div class="footer-info">
              <i class="fa fa-info-circle text-info"></i>
              <span>Template variables like <code v-pre>{{patient_first_name}}</code> will be replaced with real data. Don't modify them!</span>
            </div>
            <div class="footer-actions">
              <button type="button" class="btn btn-outline-secondary" @click="$refs.translationEditModal.hide()">
                Cancel
              </button>
              <button type="button" class="btn btn-primary" @click="saveTranslationEdit" :disabled="translationLoading">
                <i class="fa fa-save"></i> Save {{ editingTranslationLang ? editingTranslationLang.name : '' }} Translation
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
// https://github.com/codex-team/editor.js/issues/676



import Multiselect from "vue-multiselect";
import { compressImageSimple } from "@/utils/imageCompression";
//import { Editor } from 'vue-editor-js'
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CustomEditor from "@/components/Editor/Editor.vue";
import CommonOverlay from "@/components/CommonOverlay.vue";
import SmsGuidance from "@/components/SmsGuidance.vue";
import { getSmsGuidance } from "@/utils/smsGuidance";
import { findUnknownVariables, describeUnknownVariables, isReviewRequestTagged, REVIEW_SAMPLE_VARS } from "@/utils/templateVariables";
import { extractSmsTextFromBlocks } from "@/utils/smsTemplateText";
import { mapActions } from 'vuex';
import _ from 'lodash';
import axios from 'axios';
import mixin from '../../mixin.js';
// CHANGE #1: Adding CustomLink import for WikiEdit functionality (same as WikiCreate)
import CustomLink from "@/components/Editor/custom-link/index.js";
import CustomExternalLink from "@/components/Editor/custom-external-link/index.js";
import { pickVerifiedFromEmail } from "@/utils/verifiedSender.js";

// Ensure environment-aware API routing for component-level axios usage
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

const EMOJI_TABLE = {
    'Frequently Used': { thumbs_up: '👍', heart: '❤️', smile: '😄', wave: '👋', pray: '🙏', party: '🎉', star: '⭐', check: '✅', fire: '🔥', clap: '👏' },
    'Smileys': { grinning: '😀', smiley: '😃', smile: '😄', grin: '😁', laughing: '😆', blush: '😊', wink: '😉', heart_eyes: '😍', kissing_heart: '😘', thinking: '🤔', neutral: '😐', relieved: '😌', sunglasses: '😎', cry: '😢', sob: '😭', joy: '😂' },
    'Gestures': { thumbs_up: '👍', thumbs_down: '👎', ok_hand: '👌', wave: '👋', clap: '👏', raised_hands: '🙌', pray: '🙏', muscle: '💪', point_right: '👉', point_left: '👈' },
    'Celebrations': { party: '🎉', confetti: '🎊', balloon: '🎈', gift: '🎁', cake: '🎂', trophy: '🏆', sparkles: '✨', star: '⭐', tada: '🎊', ribbon: '🎀' },
    'Hearts & Symbols': { red_heart: '❤️', orange_heart: '🧡', yellow_heart: '💛', green_heart: '💚', blue_heart: '💙', purple_heart: '💜', check: '✅', x: '❌', exclamation: '❗', question: '❓' }
};

  export default {
    name: 'WikiEdit',
    components: { Multiselect, CustomEditor, ErrorDisplay, CommonOverlay, SmsGuidance },
    props: ['wikiPageID'],
    mixins: [mixin],

    created() {
      this.init();
    },

    // NEW: Add watch for route changes (this will help with SPA navigation)
    watch: {
      '$route'(to, from) {
        // Re-initialize when the route changes
        if (to.params.wikiPageID !== from.params.wikiPageID) {
          this.init();
        }
      },
      
      // No longer need per-language watchers — translations are auto-managed
    },

    data() {
      const vm = this; // capture Vue instance for upload overlay toggle
      return {
        show: false,
        isUploadingImage: false,
        wikiName: '',
        wikiNameError: false,
        wikiPage: null,
        wikiPages: null,
        forms: [],
        packets: [],  // Form Packets for this site. Used by CustomLink picker + existing-link readable-name lookups.
        isEmailTemplate: false,
        isSmsTemplate: false,
        emailSubject: '',  // Custom email subject line for email templates
        emailSubjectError: false,
        isPublic: false,  // Public accessibility flag
        sendgridTemplateID: null,
        siteTags: [],
        teamioTags: [],
        selectedTags: [],
        deletedImageUrls: [],
        // Translation properties
        translationLanguages: [
          { code: 'spa', name: 'Spanish', flag: '🇪🇸', awsCode: 'es', timeLabel: 'Ahora' },
          { code: 'rus', name: 'Russian', flag: '🇷🇺', awsCode: 'ru', timeLabel: 'Сейчас' }
        ],
        selectedTranslationLanguages: [],  // User-selected language codes, e.g. ['spa'] or ['spa', 'rus']
        translationStatuses: {},
        translationPreviewContents: {},
        translationLoading: false,
        showInlinePreview: true,  // Open by default for SMS preview
        showEmailPreview: true,   // Open by default for email preview
        liveEditorContent: null,  // Stores live editor content for real-time preview
        previewUpdateTimeout: null,  // Debounce timer for preview updates
        // Translation edit modal state
        translationEditorLoading: false,
        editingTranslationLang: null,
        translationEditBlocks: [],
        englishPreviewBlocks: [],
        editorTools: {
          header: {
            inlineToolbar: ["marker", "italic", "bold", "link", "wikiLink"]
          },
          // Use official @editorjs/image with S3 upload configuration
          image: {
            config: {
              uploader: {
                async uploadByFile(file) {
                  vm.isUploadingImage = true;
                  try {
                    // Compress image before upload
                    const compressedFile = await compressImageSimple(file);

                    const formData = new FormData();

                    // Sanitize filename: replace special chars that break S3 copy/URL resolution
                    const safeName = file.name.replace(/[&#+;?@!$%^={}|\\<>'"`,()[\]]/g, '_').replace(/ /g, '-').replace(/-{2,}/g, '-').replace(/_{2,}/g, '_');

                    const requestData = {
                      siteID: window.localStorage.teamioSiteID,
                      name: safeName,
                      type: compressedFile.type || file.type,
                      teamioType: 'WIKI'
                    };

                    // Get upload URL from your API (environment-aware via axios baseURL)
                    const { data: uploadData } = await axios.post('/requestUploadURL', requestData, {
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + window.localStorage.teamioAuthToken
                      }
                    });

                    // Handle both possible response structures
                    let uploadURL, uploadFields;
                    if (uploadData.uploadURL) {
                      // New structure: {uploadURL: {url: '...', fields: {...}}}
                      uploadURL = uploadData.uploadURL.url;
                      uploadFields = uploadData.uploadURL.fields;
                    } else if (uploadData.url && uploadData.fields) {
                      // Direct structure: {url: '...', fields: {...}}
                      uploadURL = uploadData.url;
                      uploadFields = uploadData.fields;
                    } else {
                      throw new Error('Invalid upload URL response structure');
                    }

                    // Upload to S3
                    Object.keys(uploadFields).forEach(key => {
                      formData.append(key, uploadFields[key]);
                    });
                    formData.append('file', compressedFile);

                    const uploadResponse = await fetch(uploadURL, {
                      method: 'POST',
                      body: formData
                    });

                    if (!uploadResponse.ok) {
                      throw new Error('S3 upload failed');
                    }

                    // Return success response in Editor.js format
                    // Construct the final S3 URL properly
                    const finalImageUrl = uploadURL.endsWith('/')
                      ? `${uploadURL}${uploadFields.key}`
                      : `${uploadURL}/${uploadFields.key}`;

                    return {
                      success: 1,
                      file: {
                        url: finalImageUrl,
                        name: safeName,
                        size: compressedFile.size
                      }
                    };
                  } catch (error) {
                    console.error('S3 upload error:', error);
                    throw error;
                  } finally {
                    vm.isUploadingImage = false;
                  }
                },
                async uploadByUrl(url) {
                  // Reject non-HTTP URLs
                  if (!url || !url.match(/^https?:\/\/.+/i)) {
                    return { success: 0, message: 'Invalid image URL' };
                  }

                  vm.isUploadingImage = true;
                  try {
                    // Download the external image and re-upload to our S3
                    // This prevents data loss if the external URL expires (e.g. Scribe signed URLs)
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

                    const blob = await response.blob();

                    // Extract a clean filename from the URL path
                    let fileName;
                    try {
                      const urlPath = new URL(url).pathname;
                      fileName = decodeURIComponent(urlPath.split('/').pop() || '');
                    } catch (e) {
                      fileName = '';
                    }
                    if (!fileName || fileName === '/' || !fileName.includes('.')) {
                      fileName = `pasted-image-${Date.now()}.png`;
                    }
                    // Sanitize filename for S3 compatibility
                    fileName = fileName.replace(/[&#+;?@!$%^={}|\\<>'"`,()[\]]/g, '_').replace(/ /g, '-').replace(/-{2,}/g, '-').replace(/_{2,}/g, '_');

                    const file = new File([blob], fileName, { type: blob.type || 'image/png' });
                    const compressedFile = await compressImageSimple(file);

                    // Use the same presigned-URL upload flow as uploadByFile
                    const requestData = {
                      siteID: window.localStorage.teamioSiteID,
                      name: fileName,
                      type: compressedFile.type || file.type,
                      teamioType: 'WIKI'
                    };

                    const { data: uploadData } = await axios.post('/requestUploadURL', requestData, {
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + window.localStorage.teamioAuthToken
                      }
                    });

                    let uploadURL, uploadFields;
                    if (uploadData.uploadURL) {
                      uploadURL = uploadData.uploadURL.url;
                      uploadFields = uploadData.uploadURL.fields;
                    } else if (uploadData.url && uploadData.fields) {
                      uploadURL = uploadData.url;
                      uploadFields = uploadData.fields;
                    } else {
                      throw new Error('Invalid upload URL response structure');
                    }

                    const formData = new FormData();
                    Object.keys(uploadFields).forEach(key => {
                      formData.append(key, uploadFields[key]);
                    });
                    formData.append('file', compressedFile);

                    const uploadResponse = await fetch(uploadURL, {
                      method: 'POST',
                      body: formData
                    });

                    if (!uploadResponse.ok) throw new Error('S3 upload failed');

                    const finalImageUrl = uploadURL.endsWith('/')
                      ? `${uploadURL}${uploadFields.key}`
                      : `${uploadURL}/${uploadFields.key}`;

                    return {
                      success: 1,
                      file: {
                        url: finalImageUrl,
                        name: fileName,
                        size: compressedFile.size
                      }
                    };
                  } catch (error) {
                    console.warn('uploadByUrl: Could not re-upload to S3, falling back to external URL.', error);
                    // Fallback: preserve the external URL rather than losing the image entirely
                    return { success: 1, file: { url: url } };
                  } finally {
                    vm.isUploadingImage = false;
                  }
                }
              }
            }
          },
          // Temporarily disable problematic tools to isolate toolbox issue
          // image: {
          //   config: {
          //     uploader:{}
          embed: {
            config: {
              services: {
                youtube: {
                  // Custom regex to handle YouTube URLs with extra query parameters
                  regex: /(?:https?:\/\/)?(?:www\.)?(?:(?:youtu\.be\/)|(?:youtube\.com)\/(?:v\/|u\/\w\/|embed\/|watch))(?:(?:\?v=)?([^#&?=\s]+))(?:[?&][^#\s]*)?/,
                  embedUrl: 'https://www.youtube.com/embed/<%= remote_id %>',
                  html: '<iframe style="width:100%;" height="320" frameborder="0" allowfullscreen></iframe>',
                  height: 320,
                  width: 600
                },
                vimeo: true,
                coub: true,
                codepen: true,
                instagram: true,
                twitter: true,
                gfycat: true,
                imgur: true,
                pinterest: true,
                github: true
              }
            }
          },
          list: {
            inlineToolbar: true
          },
          delimiter: {},
          table: {
            inlineToolbar: true,
            config: {
              rows: 2,
              cols: 3
            }
          },
          marker: {
            shortcut: "CMD+SHIFT+M"
          },
          inlineCode: {
            shortcut: "CMD+SHIFT+C"
          },
          // Re-enable paragraph tool with working fallback implementation
          paragraph: {
            inlineToolbar: ["marker", "italic", "bold", "link", "wikiLink"],
            placeholder: 'Type here...'
          },
          checklist: {
            inlineToolbar: true
          },
          // CHANGE #2: Re-enabling WikiLink tool with CustomLink class
          wikiLink: {
            class: CustomLink,
            inlineToolbar: true
          },
          // Override built-in link tool with wider input + OK/Cancel buttons
          link: {
            class: CustomExternalLink
          }
        },
        value: [],
        simpleDropdownVariant: "Select",
        isPickerActive: false,
        availableVariables: [
          { value: '{{patient_first_name}}', primary: false },
          { value: '{{patient_last_name}}', primary: false },
          { value: '{{guarantor_first_name}}', primary: false },
          { value: '{{guarantor_last_name}}', primary: false },
          { value: '{{apt_date}}', primary: false },
          { value: '{{apt_dayofweek}}', primary: false },
          { value: '{{family_appts}}', primary: false },
          { value: '{{confirm_button}}', primary: false },
          { value: '{{site_name}}', primary: false },
          { value: '{{site_address1}}', primary: false },
          { value: '{{site_address2}}', primary: false },
          { value: '{{site_city}}', primary: false },
          { value: '{{site_state}}', primary: false },
          { value: '{{site_zip}}', primary: false },
          { value: '{{site_officephone}}', primary: false },
          // Email Signature — the office's signature block (logo, name, phone, address,
          // website, socials) configured under Settings -> Email. EMAIL ONLY: it resolves to
          // an HTML block, so wiki-service normalizes it to triple-brace at bake time and the
          // SMS paths strip it. Keep in sync with WikiCreate.vue, WikiImportModal.vue,
          // utils/templateVariables.js and wiki-service TEAMIO_VARIABLES.
          { value: '{{email_signature_block}}', label: '{{email_signature_block}} (Email Signature)', primary: false },
          // {{employee_name}} is intentionally absent: it still RESOLVES (permanent legacy
          // alias) but offering it alongside {{employee_full_name}} would show two chips that
          // do the same thing. Keep in sync with wiki-service TEAMIO_VARIABLES.
          { value: '{{employee_full_name}}', primary: false },
          { value: '{{employee_first_name}}', primary: false },
          { value: '{{payment_link}}', primary: true },
          { value: '{{patient_balance}}', primary: true },
          { value: '{{total_amount}}', primary: true },
          { value: '{{recall_due_date}}', primary: false },
          { value: '{{online_apttype}}', primary: false },
          { value: '{{apt_provider}}', primary: false }
        ],
        // Shown only on pages tagged review-request: only the Reviews send path fills it
        // (utils/templateVariables REVIEW_TEMPLATE_VARIABLES). Keep in sync with WikiCreate.vue.
        reviewVariables: [
          { value: '{{review_link}}', primary: true }
        ],
        emojiTable: EMOJI_TABLE,
        savedRange: null,
        savedBlockIndex: null
      };
  },

  mounted() {
    this._selectionHandler = () => this.saveEditorSelection();
    document.addEventListener('selectionchange', this._selectionHandler);
  },

  beforeDestroy() {
    if (this._selectionHandler) {
      document.removeEventListener('selectionchange', this._selectionHandler);
    }
  },

  methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
      ...mapActions('wiki', ['getWikiPage', 'updateWiki', 'getWikiPages', 'getSiteTags', 'getWikiTranslation', 'saveManualTranslation', 'translateWikiNow']),
      ...mapActions('forms', ['getPackets']),

      /**
       * Save the current selection/cursor position inside the EditorJS editor.
       * Called on every selectionchange event so we always have the last known
       * cursor position, even after the editor loses focus to the dropdown.
       */
      saveEditorSelection() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const editorEl = this.$refs.customEditor?.$el;
        if (!editorEl) return;

        // Only save if the selection is inside the editor
        if (editorEl.contains(sel.anchorNode)) {
          this.savedRange = sel.getRangeAt(0).cloneRange();
          const editor = this.$refs.customEditor?.editor;
          if (editor) {
            this.savedBlockIndex = editor.blocks.getCurrentBlockIndex();
          }
        }
      },

      /**
       * Insert a variable at the current cursor position in the editor
       * @param {string} variable - The variable text to insert (e.g., '{{patient_first_name}}')
       */
      async insertVariable(variable) {
        try {
          const editor = this.$refs.customEditor?.editor;
          if (!editor) {
            console.warn('Editor not ready');
            return;
          }

          // Use saved range if available (cursor position before dropdown stole focus)
          if (this.savedRange) {
            const range = this.savedRange;

            // Find the contenteditable element that contains this range
            const blockIndex = this.savedBlockIndex >= 0 ? this.savedBlockIndex : editor.blocks.getCurrentBlockIndex();
            if (blockIndex >= 0) {
              const block = editor.blocks.getBlockByIndex(blockIndex);
              if (block) {
                const editableElement = block.holder.querySelector('[contenteditable="true"]');
                if (editableElement) {
                  // Focus the element first so EditorJS tracks the block
                  editableElement.focus();

                  // Restore the saved range
                  const sel = window.getSelection();
                  sel.removeAllRanges();
                  sel.addRange(range);

                  // Insert at cursor position
                  range.deleteContents();
                  const textNode = document.createTextNode(variable);
                  range.insertNode(textNode);

                  // Move cursor after inserted text
                  range.setStartAfter(textNode);
                  range.setEndAfter(textNode);
                  sel.removeAllRanges();
                  sel.addRange(range);

                  // Save updated position
                  this.savedRange = range.cloneRange();

                  // Trigger change event
                  editableElement.dispatchEvent(new Event('input', { bubbles: true }));

                  // Update the preview if SMS template
                  if (this.isSmsTemplate) {
                    this.debouncedUpdatePreview();
                  }
                  return;
                }
              }
            }
          }

          // Fallback: no saved range — insert a new paragraph block
          await editor.blocks.insert('paragraph', { text: variable });

          // Update the preview if SMS template
          if (this.isSmsTemplate) {
            this.debouncedUpdatePreview();
          }
        } catch (error) {
          console.error('Error inserting variable:', error);
        }
      },

      async insertEmoji(emoji) {
        await this.insertVariable(emoji);
      },

      async init() {
        let res = await this.getWikiPages({siteID: this.siteID});

        // Fetch Form Packets in parallel with the wiki-page lookup. Kept separate from `this.forms`
        // since other code paths here expect `this.forms` to be forms-only.
        try {
          const packetsRes = await this.getPackets({ siteID: this.siteID });
          this.packets = Array.isArray(packetsRes) ? packetsRes : [];
        } catch (err) {
          console.error('Failed to load packets for WikiEdit:', err);
          this.packets = [];
        }

        if(res.length > 0) {
          this.wikiPages = res[0];
          this.forms = res[3];
          this.wikiPage = this.wikiPages.filter(w => w.WikiPageID == this.wikiPageID)[0];
          
          if(this.wikiPage) {
            
            if(this.wikiPage.SendgridTemplateID && this.wikiPage.SendgridTemplateID !== "undefined")
              this.sendgridTemplateID = this.wikiPage.SendgridTemplateID;
            if(this.wikiPage.IsEmailTemplate === 1)
              this.isEmailTemplate = true;
            if(this.wikiPage.IsSmsTemplate === 1)
              this.isSmsTemplate = true;
            // Set public accessibility flag from database
            if(this.wikiPage.IsPublic === 1 || this.wikiPage.IsPublic === true)
              this.isPublic = true;
            // Load wiki name
            if(this.wikiPage.Name)
              this.wikiName = this.wikiPage.Name;
            // Load custom email subject if set
            if(this.wikiPage.EmailSubject)
              this.emailSubject = this.wikiPage.EmailSubject;
            this.siteTags = res[1];
            this.teamioTags = res[2];

            // Build a merged list so the WikiLink picker shows both forms and packets.
            // Packets are flagged with `_isPacket: true` so CustomLink renders a PACKET badge
            // and inserts `{{patient_link_for_packet_<ID>}}` instead of the form variable.
            const formsForLink = [
              ...(this.forms || []).map(f => ({ ...f, _isPacket: false })),
              ...this.packets.map(p => ({ Name: p.Name, PacketID: p.PacketID, _isPacket: true })),
            ];

            // Configure WikiLink tool
            this.editorTools.wikiLink.config = {
                wikiPages: this.wikiPages,
                forms: formsForLink,
                packets: this.packets,
                mode: process.env.VUE_APP_NODE_ENV,
            }

            // Pre-check language checkboxes from stored EnabledLanguages
            if (this.wikiPage.EnabledLanguages) {
              try {
                this.selectedTranslationLanguages = JSON.parse(this.wikiPage.EnabledLanguages);
              } catch (e) {
                this.selectedTranslationLanguages = [];
              }
            }

            // Load translation statuses and preview content for all languages
            await this.loadAllTranslationStatuses();

          } else {
            this.$toast.error('No wiki page found!');
            return;
          }
        } else {
          // No wiki data received from API
        }
      },

      // ============================================================================
      // TRANSLATION METHODS (language-agnostic)
      // ============================================================================

      async loadAllTranslationStatuses() {
        for (const lang of this.translationLanguages) {
          try {
            const result = await this.getWikiTranslation({ wikiPageID: this.wikiPageID, languageCode: lang.code });
            if (result && result.exists !== false) {
              this.$set(this.translationStatuses, lang.code, result);
              // Also load preview content if available
              if (result.Content) {
                this.$set(this.translationPreviewContents, lang.code, result.Content);
              }
            }
          } catch (err) {
            console.log(`Error loading ${lang.code} translation status:`, err);
          }
        }
      },

      formatTranslationDate(dateStr) {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      },

      getBlockTypeLabel(type) {
        const labels = {
          'header': 'Title',
          'paragraph': 'Text',
          'list': 'List'
        };
        return labels[type] || type;
      },

      getBlockIcon(type) {
        const icons = {
          'header': 'fa fa-header',
          'paragraph': 'fa fa-paragraph',
          'list': 'fa fa-list'
        };
        return icons[type] || 'fa fa-file-text-o';
      },

      async togglePreviewSection() {
        this.showInlinePreview = !this.showInlinePreview;
      },

      async retranslateLanguage(langCode) {
        const lang = this.translationLanguages.find(l => l.code === langCode);
        const langName = lang ? lang.name : langCode;
        const status = this.translationStatuses[langCode];

        if (status && status.ManuallyEdited) {
          const confirmed = await this.$bvModal.msgBoxConfirm(
            `This will overwrite your manual ${langName} edits. Are you sure?`,
            { title: 'Confirm Re-translation', okVariant: 'warning', okTitle: 'Re-translate' }
          );
          if (!confirmed) return;
        }

        try {
          this.translationLoading = true;
          await this.translateWikiNow({ wikiPageID: this.wikiPageID, languageCode: langCode });
          // Reload this language's status and content
          const result = await this.getWikiTranslation({ wikiPageID: this.wikiPageID, languageCode: langCode });
          if (result && result.exists !== false) {
            this.$set(this.translationStatuses, langCode, result);
            if (result.Content) {
              this.$set(this.translationPreviewContents, langCode, result.Content);
            }
          }
          this.$toast.success(`${langName} translation updated!`);
        } catch (err) {
          console.log(`Error re-translating ${langCode}:`, err);
          this.$toast.error(`Failed to re-translate ${langName}`);
        } finally {
          this.translationLoading = false;
        }
      },

      async showTranslationEditor(langCode) {
        const lang = this.translationLanguages.find(l => l.code === langCode);
        if (!lang) return;

        const status = this.translationStatuses[langCode];
        if (!status) {
          this.$toast.info(`No ${lang.name} translation yet. Save the page first to auto-translate.`);
          return;
        }

        this.editingTranslationLang = lang;
        this.$refs.translationEditModal.show();
        this.translationEditorLoading = true;

        try {
          // Load English content for comparison
          if (this.wikiPage && this.wikiPage.Content) {
            const englishContent = JSON.parse(this.wikiPage.Content);
            this.englishPreviewBlocks = englishContent.blocks || [];
          }

          // Fetch translation content
          const result = await this.getWikiTranslation({ wikiPageID: this.wikiPageID, languageCode: langCode });
          if (result && result.Content) {
            const translatedContent = JSON.parse(result.Content);
            this.translationEditBlocks = JSON.parse(JSON.stringify(translatedContent.blocks || []));
          }
        } catch (err) {
          console.log(`Error loading ${langCode} editor:`, err);
          this.$toast.error(`Failed to load ${lang.name} translation for editing`);
          this.$refs.translationEditModal.hide();
        } finally {
          this.translationEditorLoading = false;
        }
      },

      async saveTranslationEdit() {
        if (!this.editingTranslationLang) return;
        const langCode = this.editingTranslationLang.code;
        const langName = this.editingTranslationLang.name;

        try {
          this.translationLoading = true;

          const translatedContent = {
            time: new Date().getTime(),
            blocks: this.translationEditBlocks,
            version: "2.26.4"
          };

          await this.saveManualTranslation({
            wikiPageID: this.wikiPageID,
            languageCode: langCode,
            Content: JSON.stringify(translatedContent)
          });

          this.$toast.success(`${langName} translation saved!`);

          // Refresh status and preview content
          const result = await this.getWikiTranslation({ wikiPageID: this.wikiPageID, languageCode: langCode });
          if (result && result.exists !== false) {
            this.$set(this.translationStatuses, langCode, result);
            if (result.Content) {
              this.$set(this.translationPreviewContents, langCode, result.Content);
            }
          }

          this.$refs.translationEditModal.hide();
        } catch (err) {
          console.log(`Error saving ${langCode} translation:`, err);
          this.$toast.error(`Failed to save ${langName} translation`);
        } finally {
          this.translationLoading = false;
        }
      },

      addTag(newTag) {
        // Normalize before matching: trim + lowercase. Prevents creating
        // duplicate rows in teamio.tags when the user re-types an existing
        // tag name with different casing or whitespace.
        const norm = String(newTag || '').trim().toLowerCase();
        if (!norm) return;
        const tagExistsInTeamio = this.teamioTags.find(
          t => String(t.Tag || '').trim().toLowerCase() === norm
        );

        const tag = {
          Tag: tagExistsInTeamio ? tagExistsInTeamio.Tag : String(newTag).trim(),
          TagID: tagExistsInTeamio ? tagExistsInTeamio.TagID : ''
        };

        const existsIndex = this.selectedTags.findIndex(
          t => String(t.Tag || '').trim().toLowerCase() === norm
        );
        if(existsIndex === -1)
          this.selectedTags.push(tag);
      },

      removeTag(tagName) {
        let index1 = this.selectedTags.findIndex(t => t.Tag === tagName);
        if(index1 !== -1)
          this.selectedTags.splice(index1,1);
      },

      updateTemplateTag(type) {
        if(type === "email") {
          if(!this.isEmailTemplate)
            this.addTag('email-template');
          else {
            this.removeTag('email-template');
          }
        } else if(type === "sms") {
          if(!this.isSmsTemplate)
            this.addTag('sms-template');
          else {
            this.removeTag('sms-template');
          }
        }
      },

      onSelect (option) {
        if (option === 'Disable me!') this.isDisabled = true
      },

      onChange (value) {
        this.value = value
        if (value.indexOf('Reset me!') !== -1) this.value = []
      },

      stripHtml(html)
      {
        let tmp, ret, searchRegExp, replaceWith;
        tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
      },

      stripHtmlForPreview(html) {
        // For phone preview, keep some formatting but strip complex HTML
        if (!html) return '';
        // Replace <br> with actual line breaks for display
        let text = html.replace(/<br\s*\/?>/gi, '<br>');
        // Keep links visible but simplified
        text = text.replace(/<a[^>]*>([^<]*)<\/a>/gi, '$1');
        // Strip other HTML tags
        const tmp = document.createElement("DIV");
        tmp.innerHTML = text;
        return tmp.innerHTML;
      },

      // Plain SMS text from the editor data being saved — reads the definitive `e` passed to
      // save() (not the live computed) so the save-gate measures exactly what's persisted.
      // Delegates to the shared FE/BE twin so this agrees with the backend renderer: header
      // blocks are dropped as titles UNLESS they're all the template has, in which case they
      // are the message (see src/utils/smsTemplateText.js — PROD outage 2026-08-04).
      extractSmsTextFromEditorData(data) {
        if (!data) return '';
        return extractSmsTextFromBlocks(data.blocks).trim();
      },

      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
      },

      replaceAll(str, find, replace) {
        return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
      },

      // Removed saveViaEditor method - using only CustomEditor's save button

      async save(e) {
        // Validate wiki name is filled out
        if (!this.wikiName || this.wikiName.trim() === '') {
          this.wikiNameError = true;
          this.$toast.error("Please enter a wiki name before saving.");
          return;
        }

        // Validate email subject if email template is selected
        if (this.isEmailTemplate && (!this.emailSubject || this.emailSubject.trim() === '')) {
          this.emailSubjectError = true;
          this.$toast.error("Please enter an email subject before saving.");
          return;
        }

        // Unknown-variable gate — ANY message template, SMS or email. Twin of WikiCreate.vue.
        //
        // The Wiki is one authoring surface: the same + Insert palette serves both, and a page
        // can be flagged as both at once. Email is NOT a SendGrid-only world — utility.js:5128
        // runs emailHtml through the very same replaceTemplateVariables, which substitutes only
        // TRUTHY values, so an unrecognised {{token}} reaches the recipient verbatim either way.
        // The pasted review-request pack proved it: "Review Request SMS" and "Review Request
        // Email" both carry {{first_name}} / {{practice_name}} / {{review_link}}.
        //
        // Deliberately NOT run on ordinary wiki pages — 2 live prod pages are documentation that
        // mentions "{{" in prose, and blocking those would be a gate on something that works.
        //
        // Detection runs over the SERIALISED editor payload, so a variable typed into a header
        // or list block is caught too — header-only templates are what the 2026-08-04 outage was.
        // The Reviews variables pass only on review-request pages (their one send path).
        if (this.isSmsTemplate || this.isEmailTemplate) {
          const unknownVars = findUnknownVariables(JSON.stringify(e), { tags: this.selectedTagNames });
          if (unknownVars.length) {
            this.$toast.error(describeUnknownVariables(unknownVars));
            return; // matches the sibling gates — `show` is not toggled until after them
          }
        }

        // SMS template save-gate (shared utils/smsGuidance — the PRIMARY defense against
        // sending over-limit texts). Hard-block a template whose sample render exceeds the
        // carrier segment cap; a costly / near-cap one only shows the inline warning banner
        // (real patient data is substituted at send time and can push a near-cap template
        // over, where the backend then blocks it and the patient silently misses it).
        if (this.isSmsTemplate) {
          const smsText = this.extractSmsTextFromEditorData(e);
          // Empty-body gate. There was only ever a MAXIMUM length check here, so a template with
          // no text saved happily and then failed at send time — on 2026-08-04 that crashed the
          // send worker and stalled a practice's whole message queue. An SMS template with nothing
          // to say is never valid; block it at the source.
          if (!smsText || smsText.trim() === '') {
            this.$toast.error("This SMS template has no message text. Add the text you want texted to patients before saving.");
            return;
          }
          const g = getSmsGuidance(smsText, {
            substituteTags: true,
            vars: this.isReviewRequestTemplate ? REVIEW_SAMPLE_VARS : undefined,
          });
          if (g.blocked) {
            this.$toast.error(`${g.title}. ${g.message}`);
            return; // never save an over-carrier-limit SMS template
          }
          // A warn state (long / emoji / near the limit) is NOT a prompt — the <SmsGuidance>
          // banner under the editor already shows it while typing. Save just saves.
        }

        this.show = !this.show;
        this.setErrored(true);
        this.setMessage(``);

        // Clean up problematic &nbsp; sequences but preserve necessary spaces
        let allpara = e.blocks.filter(b => b.type === 'paragraph');
        let _this = this;
        allpara.forEach(function(b) {
          // Only remove multiple consecutive &nbsp; or ones at start/end
          // Preserve single &nbsp; that might be intentional spacing
          b.data.text = b.data.text
            .replace(/&nbsp;{2,}/g, ' ')  // Multiple &nbsp; -> single space
            .replace(/^&nbsp;|&nbsp;$/g, '') // Remove from start/end
            .replace(/&nbsp;(?=<)/g, ' ')  // Before HTML tags
            .replace(/(?=>)&nbsp;/g, ' '); // After HTML tags
        });

        // Clean enhanced links back to template syntax before saving
        this.cleanEnhancedLinks(e);

        // Send all images in the content - backend will handle processing correctly
        // This ensures images don't disappear after save
        const uploadedImages = _.filter(e.blocks, {"type": "image"})
          .map(function(object) {
            return _.get(object, 'data.file.url');
          })
          .filter(function(imageUrl) {
            // Include all valid image URLs - let backend determine what needs processing
            return imageUrl && imageUrl.trim() !== '';
          });

        let payload = {
          WikiPageID: this.wikiPageID,
          ...(this.sendgridTemplateID && { SendgridTemplateID: this.sendgridTemplateID }),
          IsEmailTemplate: this.isEmailTemplate,
          IsSmsTemplate: this.isSmsTemplate,
          IsPublic: this.isPublic,  // Public accessibility flag
          EmailSubject: this.emailSubject,  // Custom email subject line for email templates
          PageToUpdate : {
            Name: this.wikiName.trim(),
            Content: JSON.stringify(e),
            SiteID: this.siteID
          },
          Tags: this.selectedTags.length > 0? this.selectedTags : null,
          UploadedImages: uploadedImages,
          deletedImageUrls: this.deletedImageUrls.length > 0? this.deletedImageUrls : null,
          SiteID: this.siteID,
          employeesWhoCanEdit : this.employeesWhoCanEdit,
          AllEmployeesCanSee: this.allEmployeesCanSee === "false" ? 0 : 1,
          selectedLanguages: (this.isSmsTemplate || this.isEmailTemplate)
              ? this.selectedTranslationLanguages
              : []
        };


        try {
          const response = await this.updateWiki(payload);
          this.show = !this.show;
          
          // Check for translation results (only show warnings/errors, not success)
          if (response && response.data && response.data.translationResults) {
            for (const [langCode, result] of Object.entries(response.data.translationResults)) {
              if (result.success) {
                this.$set(this.translationStatuses, langCode, result);
              }
              if (result.warning) {
                const lang = this.translationLanguages.find(l => l.code === langCode);
                this.$toast.warning(`${lang ? lang.name : langCode}: manual translation preserved (English changed)`);
              } else if (result.error) {
                const lang = this.translationLanguages.find(l => l.code === langCode);
                this.$toast.warning(`${lang ? lang.name : langCode} translation failed: ${result.error}`);
              }
            }
            // Reload preview contents
            await this.loadAllTranslationStatuses();
          }
          
          this.$toast.success("Wiki page updated successfully!");
        } catch (error) {
          console.error("Wiki update error:", error);
          this.show = !this.show;
          this.$toast.error("Error updating wiki page. Please try again later");
        }
      },

      onReady() {
        var elements = document.querySelectorAll('[contenteditable=false]');
        elements.forEach(element => {
          element.setAttribute('contenteditable' , true);
        });
        
        // Try to enhance links when editor is ready
        setTimeout(() => {
          this.enhanceExistingLinks();
        }, 1000);
      },

      async onChange(event) {
        // Debounce the preview update to avoid too many calls
        if (this.previewUpdateTimeout) {
          clearTimeout(this.previewUpdateTimeout);
        }
        
        this.previewUpdateTimeout = setTimeout(async () => {
          try {
            if (this.$refs.customEditor && this.$refs.customEditor.editor) {
              const savedData = await this.$refs.customEditor.editor.save();
              if (savedData && savedData.blocks) {
                this.liveEditorContent = savedData;
              }
            }
          } catch (e) {
            console.log('Preview update error:', e);
          }
        }, 300); // 300ms debounce
        
        //console.dir(event);
        if(event.type === 'block-removed') {
          let target = event.detail.target;
          if(target.name === 'image') {
            let blockId = target.id;
    
            
            let block = this.editorData.blocks.find(b => b.id === blockId);
            if(block) {
              let imageRemovedUrl = block.data.file.url;
      
              this.deletedImageUrls.push(imageRemovedUrl);
            }
          }
        }
      },

      // Manual enhancement of existing links for WikiEdit page
      enhanceExistingLinks() {
        try {
          // Find all existing links in the editor
          const editorElement = document.querySelector('.codex-editor__redactor');
          if (!editorElement) {
            return;
          }
          
          const links = editorElement.querySelectorAll('a.custom-link, a[href*="/app/wikiview/"], a[href*="{{patient_link_for_form_"], a[href*="{{patient_link_for_packet_"]');
          

          
          // Helper function to find readable name from href
          const findReadableNameFromHref = (href) => {
            let targetId = null;
            
            // IMPORTANT: URL decode the href first to handle browser encoding
            const decodedHref = decodeURIComponent(href);
            
            // Handle wiki links: /app/wikiview/ID (handle both full URLs and relative paths)
            if (decodedHref.includes('/app/wikiview/')) {
              targetId = decodedHref.split('/app/wikiview/')[1];
              
              if (targetId && this.wikiPages) {
                // Try multiple possible field names for matching
                let wikiPage = this.wikiPages.find(page => page.guid === targetId);
                if (!wikiPage) {
                  wikiPage = this.wikiPages.find(page => page.WikiPageID === targetId);
                }
                
                if (wikiPage) {
                  const name = wikiPage.name || wikiPage.Name || wikiPage.title || wikiPage.Title;
                  return name;
                }
              }
            }
            
            // Handle form template links: {{patient_link_for_form_ID}} (use decoded href)
            if (decodedHref.includes('{{patient_link_for_form_') && decodedHref.includes('}}')) {
              const match = decodedHref.match(/\{\{patient_link_for_form_(.+?)\}\}/);
              if (match) {
                targetId = match[1];
                if (targetId && this.forms) {
                  // Try multiple possible field names for matching
                  let form = this.forms.find(f => f.guid === targetId);
                  if (!form) {
                    form = this.forms.find(f => f.FormID === targetId);
                  }
                  if (!form) {
                    form = this.forms.find(f => f.id === targetId);
                  }
                  if (!form) {
                    form = this.forms.find(f => f.GUID === targetId);
                  }
                  if (!form) {
                    form = this.forms.find(f => f.FormIDForWeb === targetId);
                  }

                  if (form) {
                    const name = form.name || form.Name || form.title || form.Title || form.FormName;
                    return name;
                  }
                }
              }
            }

            // Handle packet template links: {{patient_link_for_packet_ID}} (sibling of form block)
            if (decodedHref.includes('{{patient_link_for_packet_') && decodedHref.includes('}}')) {
              const match = decodedHref.match(/\{\{patient_link_for_packet_(.+?)\}\}/);
              if (match) {
                targetId = match[1];
                if (targetId && this.packets && this.packets.length) {
                  const packet = this.packets.find(p => p.PacketID === targetId);
                  if (packet) {
                    const name = packet.name || packet.Name || packet.title || packet.Title;
                    return name;
                  }
                }
              }
            }

            return null;
          };
          
          // Handle link clicks to navigate to the real URL
          const handleLinkClick = (event) => {
            const link = event.currentTarget;
            const realHref = link.getAttribute('data-real-href');

            if (realHref && realHref.includes('{{patient_link_for_form_')) {
              // Prevent navigation for form template links
              event.preventDefault();
              alert('This form link will work when sent to patients via email or SMS, but cannot be opened directly in the browser.');
              return false;
            }

            if (realHref && realHref.includes('{{patient_link_for_packet_')) {
              // Prevent navigation for packet template links (same behavior as form templates)
              event.preventDefault();
              alert('This form packet link will work when sent to patients via email or SMS, but cannot be opened directly in the browser.');
              return false;
            }

            if (realHref && realHref.includes('/app/wikiview/')) {
              // Allow normal navigation for wiki links
              event.preventDefault();
              window.location.href = realHref;
            }
          };
          
          links.forEach((link, index) => {
            // Skip if already processed
            if (link.getAttribute('data-real-href')) {
              return;
            }
            
            const readableName = findReadableNameFromHref(link.getAttribute('href'));

            if (readableName) {
              // Store the raw attribute value (not link.href which browser-resolves into a full URL with encoding)
              const realHref = link.getAttribute('href');
              
              const isFormTemplate = realHref.includes('{{patient_link_for_form_');
              const isPacketTemplate = realHref.includes('{{patient_link_for_packet_');

              link.setAttribute('data-real-href', realHref);
              link.setAttribute('data-link-name', readableName);
              link.setAttribute('data-link-type', isPacketTemplate ? 'packet' : (isFormTemplate ? 'form' : 'wiki'));

              let titleText;
              if (isPacketTemplate) titleText = `Form Packet Template: ${readableName} (works in patient emails/SMS)`;
              else if (isFormTemplate) titleText = `Form Template: ${readableName} (works in patient emails/SMS)`;
              else titleText = `Link to: ${readableName}`;
              link.setAttribute('title', titleText);
              
              // Override both href property and getAttribute method
              try {
                // Override the href property
                Object.defineProperty(link, 'href', {
                  get: function() {
                    return readableName;
                  },
                  set: function(value) {
                    // Ignore attempts to set href, keep our readable name
                  },
                  configurable: true,
                  enumerable: true
                });
                
                // Override getAttribute method to return readable name for 'href'
                const originalGetAttribute = link.getAttribute;
                link.getAttribute = function(name) {
                  if (name === 'href') {
                    return readableName;
                  }
                  return originalGetAttribute.call(this, name);
                };
                
                // Also try setting the href attribute to readable name (as backup)
                link.setAttribute('href', readableName);
                
              } catch (error) {
                // Fallback: just set the attribute (even though it gets URL-ized)
                link.setAttribute('href', readableName);
              }
              
              // Add event listeners for better UX
              link.addEventListener('mouseenter', () => {
                // Could add hover effects here if needed
              });
              
              // Add click handler to navigate to real URL
              link.addEventListener('click', handleLinkClick);
            }
          });
          
        } catch (error) {
          // Error enhancing existing links in WikiEdit
        }
      },

      // Clean enhanced links back to clean template syntax before saving
      cleanEnhancedLinks(editorData) {
        try {
          editorData.blocks.forEach((block, blockIndex) => {
            if (block.type === 'paragraph' && block.data && block.data.text) {
              // Clean form template links: restore to {{patient_link_for_form_ID}} format
              let cleanedText = block.data.text;

              // CRITICAL: Strip localhost URLs - these should never be saved to database
              // Pattern: http://localhost:8080/app/wikiview/UUID -> /app/wikiview/UUID
              cleanedText = cleanedText.replace(/http:\/\/localhost:\d+\/app\/wikiview\//g, '/app/wikiview/');
              // Pattern: http://localhost:8080/anything -> remove the localhost prefix
              cleanedText = cleanedText.replace(/http:\/\/localhost:\d+\//g, '/');
              // Also handle https localhost (just in case)
              cleanedText = cleanedText.replace(/https:\/\/localhost:\d+\/app\/wikiview\//g, '/app/wikiview/');
              cleanedText = cleanedText.replace(/https:\/\/localhost:\d+\//g, '/');

              // Universal cleanup: Any link with data-real-href → restore href from the stored real URL
              // Handles clean values, URL-encoded values, and path-prefixed values from prior corrupted saves
              const enhancedLinkRegex = /<a[^>]*data-real-href="([^"]*)"[^>]*>([^<]*)<\/a>/g;
              cleanedText = cleanedText.replace(enhancedLinkRegex, (match, realHref, linkText) => {
                const decoded = decodeURIComponent(realHref);
                // Extract form template variable (strip any path prefix like /app/wikiedit/)
                const formVarMatch = decoded.match(/(\{\{patient_link_for_form_.+?\}\})/);
                if (formVarMatch) {
                  return `<a href="${formVarMatch[1]}">${linkText}</a>`;
                }
                // Extract packet template variable (sibling of form match above)
                const packetVarMatch = decoded.match(/(\{\{patient_link_for_packet_.+?\}\})/);
                if (packetVarMatch) {
                  return `<a href="${packetVarMatch[1]}">${linkText}</a>`;
                }
                // Extract wiki link path
                const wikiMatch = decoded.match(/(\/app\/wikiview\/[^\s"]+)/);
                if (wikiMatch) {
                  return `<a href="${wikiMatch[1]}">${linkText}</a>`;
                }
                // Fallback: use decoded realHref as-is
                return `<a href="${decoded}">${linkText}</a>`;
              });

              // Catch-all: Any <a> with form template, packet template, or wiki href — strip extra attributes
              // regardless of attribute order (href may come before or after class)
              const formOrWikiLinkRegex = /<a\s[^>]*href="([^"]*(?:\{\{patient_link_for_form_[^"]*|\{\{patient_link_for_packet_[^"]*|\/app\/wikiview\/[^"]*))"[^>]*>([^<]*)<\/a>/g;
              cleanedText = cleanedText.replace(formOrWikiLinkRegex, (match, href, linkText) => {
                return `<a href="${href}">${linkText}</a>`;
              });
              
              if (cleanedText !== block.data.text) {
                block.data.text = cleanedText;
              }
            }
          });
          
        } catch (error) {
          // Error cleaning enhanced links
        }
      },

      gotoCreatePage() {
        this.$router.push('/app/wikiCreate');
      },

      triggerSave() {
        // Call the editor's getOutput method which triggers save
        if (this.$refs.customEditor) {
          this.$refs.customEditor.getOutput();
        }
      },

      successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
      },

    },

    computed: {
      // allSettings comes from mixin (state.customer.settings transformed via _.keyBy)

      // The tags currently in the editor (not the saved row), so the variable gate judges
      // what is about to be stored.
      selectedTagNames() {
        return (this.selectedTags || []).map(t => t && t.Tag).filter(Boolean);
      },

      isReviewRequestTemplate() {
        return isReviewRequestTagged(this.selectedTagNames);
      },

      spanishPreviewBlocks() {
        if (!this.spanishPreviewContent) return [];
        try {
          const content = JSON.parse(this.spanishPreviewContent);
          return content.blocks || [];
        } catch (e) {
          return [];
        }
      },

      currentTime() {
        const now = new Date();
        return now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      },

      englishSmsPreview() {
        // Use live editor content if available, otherwise fall back to saved content
        let content = null;
        
        if (this.liveEditorContent && this.liveEditorContent.blocks) {
          // Real-time preview from editor
          content = this.liveEditorContent;
        } else if (this.wikiPage && this.wikiPage.Content) {
          // Fallback to saved content
          try {
            content = JSON.parse(this.wikiPage.Content);
          } catch (e) {
            return 'Error loading content';
          }
        }
        
        if (!content || !content.blocks) return 'Start typing above...';

        try {
          const renderBlock = (b) => {
            let text = (b.data && b.data.text) || '';
            // Replace wiki/form links with just short URL
            // In actual SMS, links become short URLs like https://dev.teamio.app/r/abc123
            // The link title text is NOT included in the SMS - only the URL
            text = text.replace(
              /<a[^>]*href=["'][^"']*(?:wikiview|patient_link_for_form|patient_link_for_packet)[^"']*["'][^>]*>[^<]*<\/a>/gi,
              'https://dev.teamio.app/r/...'
            );
            return this.stripHtmlForPreview(text);
          };

          // Headers are the template's title and are normally excluded from the SMS body —
          // unless they're all the template has, in which case they ARE the message. Mirrors
          // the backend renderer and src/utils/smsTemplateText.js (PROD outage 2026-08-04):
          // a header-only template used to preview as "No text content yet" while the backend
          // sends its text, hiding both the preview and the character counter from the author.
          const paragraphs = content.blocks.filter(b => b.type === 'paragraph').map(renderBlock).join('<br>');
          if (paragraphs) return paragraphs;

          const headers = content.blocks.filter(b => b.type === 'header').map(renderBlock).join('<br>');
          return headers || 'No text content yet';
        } catch (e) {
          return 'Error loading content';
        }
      },

      // Guidance severity ('ok' | 'warn' | 'block') for the SMS-template body, measured with sample
      // patient data (same as the status card). Drives hiding the phone mockup on any warn/block —
      // the status card is then the single signal (a preview of an undeliverable text would mislead).
      smsPreviewSeverity() {
        if (!this.isSmsTemplate) return 'ok';
        return getSmsGuidance(this.smsPlainText || '', { substituteTags: true }).severity;
      },

      // SMS Character counting and segmentation
      smsPlainText() {
        // Get text from editor content for accurate character counting
        // This accounts for links being converted to short URLs in actual SMS
        let content = null;

        if (this.liveEditorContent && this.liveEditorContent.blocks) {
          content = this.liveEditorContent;
        } else if (this.wikiPage && this.wikiPage.Content) {
          try {
            content = JSON.parse(this.wikiPage.Content);
          } catch (e) {
            return '';
          }
        }

        if (!content || !content.blocks) return '';

        try {
          const measureBlock = (b) => {
            let text = (b.data && b.data.text) || '';

            // Replace wiki/form links with estimated short URL length
            // Actual URL is like https://dev.teamio.app/r/abc123 (~35 chars)
            // Pattern: <a href="...">Link Text</a>
            text = text.replace(/<a[^>]*href=["'][^"']*(?:wikiview|patient_link_for_form|patient_link_for_packet)[^"']*["'][^>]*>[^<]*<\/a>/gi,
              'https://dev.teamio.app/r/abc1234'); // ~35 chars placeholder

            // Strip remaining HTML
            const tmp = document.createElement('div');
            tmp.innerHTML = text.replace(/<br\s*\/?>/gi, '\n');
            return tmp.textContent || tmp.innerText || '';
          };

          // Same header rule as englishSmsPreview / the backend renderer: a header-only template
          // IS its message, so it must be measured — otherwise the segment cap silently doesn't
          // apply to it and an over-limit template sails through the save-gate.
          const paragraphs = content.blocks.filter(b => b.type === 'paragraph').map(measureBlock).join('\n');
          if (paragraphs.trim() !== '') return paragraphs;

          return content.blocks.filter(b => b.type === 'header').map(measureBlock).join('\n');
        } catch (e) {
          return '';
        }
      },

      smsIsUnicode() {
        // Check if text contains non-GSM-7 characters
        // GSM-7 includes basic + extended character sets
        // Extended chars: | ^ € { } [ ] ~ \
        // All standard ASCII letters, numbers, common punctuation are GSM-7
        const text = this.smsPlainText;
        if (!text) return false;
        
        // GSM-7 compatible characters (basic + extended)
        // This regex matches chars that ARE GSM-7 compatible
        const gsm7Pattern = /^[A-Za-z0-9 \n\r@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\|~\[\]€]*$/;
        return !gsm7Pattern.test(text);
      },

      smsCharCount() {
        return this.smsPlainText.length;
      },

      smsCharLimit() {
        // Per-segment limit depends on encoding and whether concatenated
        if (this.smsIsUnicode) {
          return this.smsCharCount <= 70 ? 70 : 67; // Single vs concatenated
        }
        return this.smsCharCount <= 160 ? 160 : 153; // Single vs concatenated
      },

      smsMaxCharsForOneSegment() {
        // Max chars for a single SMS (before splitting)
        return this.smsIsUnicode ? 70 : 160;
      },

      smsSegmentCount() {
        const len = this.smsCharCount;
        if (len === 0) return 1;
        
        if (this.smsIsUnicode) {
          // UCS-2 encoding: 70 chars single, 67 chars per segment when split
          if (len <= 70) return 1;
          return Math.ceil(len / 67);
        } else {
          // GSM-7 encoding: 160 chars single, 153 chars per segment when split
          if (len <= 160) return 1;
          return Math.ceil(len / 153);
        }
      },

      smsCounterClass() {
        if (this.smsSegmentCount >= 4) return 'counter-danger';
        if (this.smsSegmentCount >= 2) return 'counter-split';
        if (this.smsCharCount > (this.smsIsUnicode ? 50 : 140)) return 'counter-caution';
        return 'counter-ok';
      },

      // Characters remaining in current segment
      smsCharsRemaining() {
        const len = this.smsCharCount;
        const singleLimit = this.smsIsUnicode ? 70 : 160;
        const segmentLimit = this.smsIsUnicode ? 67 : 153;

        if (len <= singleLimit) {
          // Within single SMS limit
          return singleLimit - len;
        }

        // Multi-segment: show remaining in current segment
        const currentSegmentUsed = len % segmentLimit;
        if (currentSegmentUsed === 0) {
          // Exactly at segment boundary
          return segmentLimit;
        }
        return segmentLimit - currentSegmentUsed;
      },

      // Progress percentage for visual bar
      smsProgressPercent() {
        const len = this.smsCharCount;
        if (len === 0) return 0;

        const singleLimit = this.smsIsUnicode ? 70 : 160;
        const segmentLimit = this.smsIsUnicode ? 67 : 153;

        if (this.smsSegmentCount <= 1) {
          // Single SMS - show progress toward single limit
          return Math.min((len / singleLimit) * 100, 100);
        }

        // Multi-segment: show progress across all segments
        const totalCapacity = this.smsSegmentCount * segmentLimit;
        return Math.min((len / totalCapacity) * 100, 100);
      },

      // Progress bar color class
      smsProgressClass() {
        const remaining = this.smsCharsRemaining;
        if (this.smsSegmentCount >= 4) return 'progress-danger';
        if (this.smsSegmentCount >= 2) return 'progress-warning';
        if (remaining < 20) return 'progress-caution';
        return 'progress-ok';
      },

      // Split the message into segments for preview
      smsSplitPreview() {
        const text = this.smsPlainText;
        if (!text || this.smsSegmentCount <= 1) {
          return null; // No split needed
        }
        
        const segmentSize = this.smsIsUnicode ? 67 : 153;
        const segments = [];
        
        for (let i = 0; i < text.length; i += segmentSize) {
          segments.push(text.substring(i, i + segmentSize));
        }
        
        return segments;
      },

      // Email Preview computed properties
      emailSubjectPreview() {
        // Use custom email subject if set
        if (this.emailSubject && this.emailSubject.trim() !== '') {
          return this.emailSubject;
        }

        // Otherwise, get header/title block for subject line
        let content = null;

        if (this.liveEditorContent && this.liveEditorContent.blocks) {
          content = this.liveEditorContent;
        } else if (this.wikiPage && this.wikiPage.Content) {
          try {
            content = JSON.parse(this.wikiPage.Content);
          } catch (e) {
            return 'Email Subject';
          }
        }

        if (!content || !content.blocks) return 'Email Subject';

        // Find first header block for subject
        const headerBlock = content.blocks.find(b => b.type === 'header');
        if (headerBlock && headerBlock.data && headerBlock.data.text) {
          return this.stripHtmlForPreview(headerBlock.data.text);
        }

        // Fallback to first paragraph
        const firstPara = content.blocks.find(b => b.type === 'paragraph');
        if (firstPara && firstPara.data && firstPara.data.text) {
          const text = this.stripHtml(firstPara.data.text);
          return text.length > 60 ? text.substring(0, 60) + '...' : text;
        }

        return 'Email Subject';
      },

      emailBodyPreview() {
        let content = null;
        
        if (this.liveEditorContent && this.liveEditorContent.blocks) {
          content = this.liveEditorContent;
        } else if (this.wikiPage && this.wikiPage.Content) {
          try {
            content = JSON.parse(this.wikiPage.Content);
          } catch (e) {
            return '<p class="text-muted">Start typing above...</p>';
          }
        }
        
        if (!content || !content.blocks) return '<p class="text-muted">Start typing above...</p>';
        
        // Build HTML from all blocks
        let html = '';
        content.blocks.forEach(block => {
          if (block.type === 'header' && block.data && block.data.text) {
            const level = block.data.level || 2;
            html += `<h${level} style="color: #333; margin-bottom: 10px;">${block.data.text}</h${level}>`;
          } else if (block.type === 'paragraph' && block.data && block.data.text) {
            html += `<p style="color: #555; line-height: 1.6; margin-bottom: 12px;">${block.data.text}</p>`;
          } else if (block.type === 'list' && block.data && block.data.items) {
            const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
            html += `<${tag} style="color: #555; padding-left: 20px; margin-bottom: 12px;">`;
            block.data.items.forEach(item => {
              html += `<li style="margin-bottom: 4px;">${item}</li>`;
            });
            html += `</${tag}>`;
          }
        });
        
        return html || '<p class="text-muted">No content yet</p>';
      },

      siteName() {
        // `state.customer.site` has never existed on the customer store (it holds
        // `sites` + `siteID`, not a resolved `site`), so this always fell through to
        // the placeholder and every email preview read "Your Practice". Resolve via
        // the store's getSite getter the mixin already exposes — same pattern as
        // mixin.js `dateFormatStr`. `sites` is null until bootstrap loads it, and the
        // getter dereferences it unguarded, so check before calling.
        let site = this.sites ? this.getSite(this.siteID) : null;
        if (site) {
          return site.Name || 'Your Practice';
        }
        return 'Your Practice';
      },

      // Get site's verified email for email preview "From" field.
      // Parsing lives in @/utils/verifiedSender.js (five storage shapes, two
      // disagreeing writers) — this used to hand-roll a comma-split, which missed
      // the JSON shapes. Keeps its own placeholder fallback: this is preview copy,
      // not a real send, so it should read as "yours goes here".
      siteVerifiedEmail() {
        return pickVerifiedFromEmail(this.allSettings && this.allSettings.GENERAL_VERIFIED_EMAILS)
          || 'noreply@yourpractice.com';
      },

      activeTranslationPreviews() {
        return this.translationLanguages
          .filter(lang => this.selectedTranslationLanguages.includes(lang.code))
          .map(lang => {
            let previewContent = '';
            if (this.translationPreviewContents[lang.code]) {
              try {
                const content = JSON.parse(this.translationPreviewContents[lang.code]);
                // Same header rule as the English preview and the backend renderer: the backend
                // applies the identical two-pass render to translated content, so a header-only
                // translation DOES send and must not preview as "No content".
                const paras = content.blocks
                  .filter(b => b.type === 'paragraph')
                  .map(b => this.stripHtmlForPreview(b.data.text))
                  .join('<br>');
                previewContent = paras || content.blocks
                  .filter(b => b.type === 'header')
                  .map(b => this.stripHtmlForPreview(b.data.text))
                  .join('<br>') || 'No content';
              } catch (e) {
                previewContent = 'Error loading content';
              }
            } else {
              previewContent = '<em style="color:#999">Translation will appear after save</em>';
            }
            return { ...lang, previewContent };
          });
      },

      editorData() {
        if(this.wikiPage && this.wikiPages && this.wikiPages.length > 0) {
          // load the selected tags
          let x = [];
          let wikiTags;
          let _siteTags = this.siteTags;
          if(this.wikiPage)
            wikiTags = this.wikiPage.Tags;
          if(wikiTags) {
            let tagArray = wikiTags.split(',');
            tagArray.forEach(function(tag) {
              let o = {};
              let tagIDObj = _.find(_siteTags, {'Tag': tag});
              let tagID;
              if(tagIDObj)
                tagID = tagIDObj.TagID;
              if(!tagID)
                tagID = '';
              o.TagID = tagID,
              o.Tag = tag
              x.push(o);
            });
          }
          this.selectedTags = x;
          return JSON.parse(this.wikiPage.Content);
        }
        else
          return {};
      },

    }
  };
</script>

<style lang="scss">
.available_variables {
  background-color: #f8f9fa;
  margin: 20px -20px 0;
  padding: 10px 20px;
}
</style>

<style lang="scss" scoped>
/* Wiki Edit Page - Modern Layout matching WikiView */
.wiki-edit-page {
  max-width: 100%;
}

/* Header Section */
.wiki-edit-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .back-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #718096;
      text-decoration: none;
      font-size: 14px;
      transition: color 0.2s ease;

      &:hover {
        color: #3abf94;
      }

      .fa {
        font-size: 12px;
      }
    }

    .page-title {
      font-size: 24px;
      font-weight: 700;
      color: #2d3748;
      margin: 0;
    }
  }

  .header-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    .btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      transition: all 0.2s ease;

      .fa {
        font-size: 12px;
      }
    }

    .view-btn {
      border-color: #e2e8f0;
      color: #4a5568;

      &:hover {
        background: #f7fafc;
        border-color: #cbd5e0;
      }
    }

    .btn-outline-primary {
      border-color: #3abf94;
      color: #3abf94;

      &:hover {
        background: #3abf94;
        color: white;
      }
    }

    .save-btn {
      background: linear-gradient(135deg, #3abf94 0%, #2d9a77 100%);
      border: none;
      color: white;
      box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(58, 191, 148, 0.4);
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
    }
  }
}

/* Content Wrapper - Two column layout */
.wiki-edit-content-wrapper {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 240px;
  }

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}

/* Main Content Area */
.wiki-edit-main {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

/* Sidebar */
.wiki-edit-sidebar {
  @media (max-width: 991px) {
    order: -1;
  }

  .sidebar-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;

    .sidebar-title {
      font-size: 14px;
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #e2e8f0;
    }

    .sidebar-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .sidebar-tag {
        display: inline-block;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 500;
        background: #edf2f7;
        color: #4a5568;
        border-radius: 16px;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
          background: #3abf94;
          color: white;
        }
      }
    }
  }
}

/* Wiki Name Row - Full width above settings */
.wiki-name-row {
  display: flex;
}

.wiki-name-panel {
  flex: 1;
  border-color: #c3dafe;
  background: linear-gradient(135deg, #f0f4ff 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #c3dafe;

    .panel-title {
      color: #3730a3;
    }
  }

  .required-star {
    color: #dc3545;
    font-weight: bold;
  }

  .wiki-name-input {
    font-size: 0.95rem;
    font-weight: 500;
  }
}

.wiki-name-error {
  border-color: #dc3545 !important;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%) !important;

  .panel-header {
    border-bottom-color: #dc3545 !important;
  }
}

/* Settings Row - Flexbox container for side-by-side panels */
.settings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* Base Settings Panel Styles */
.settings-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  flex: 1 1 180px;

  .panel-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    padding-bottom: 5px;
    border-bottom: 1px solid #eee;

    .panel-icon {
      font-size: 0.9rem;
      margin-right: 5px;
    }

    .panel-title {
      font-weight: 600;
      color: #333;
      font-size: 0.8rem;
    }
  }

  .panel-content {
    font-size: 0.85rem;
  }

  .compact-checkbox {
    margin-bottom: 2px !important;

    label {
      font-size: 0.8rem;
    }
  }
}

/* Tags Panel - Purple accent */
.tags-panel {
  border-color: #d6c9e8;
  background: linear-gradient(135deg, #f9f7fc 0%, #fff 100%);
  min-width: 200px;

  .panel-header {
    border-bottom-color: #d6c9e8;

    .panel-title {
      color: #6f42c1;
    }
  }

  .tags-content {
    .multiselect {
      font-size: 0.8rem;
      min-height: 32px;

      .multiselect__tags {
        min-height: 32px;
        padding: 4px 30px 0 6px;
        font-size: 0.8rem;
      }

      .multiselect__placeholder {
        font-size: 0.8rem;
        padding-top: 2px;
      }

      .multiselect__tag {
        font-size: 0.75rem;
        padding: 3px 20px 3px 6px;
        margin-bottom: 2px;
      }

      .multiselect__input {
        font-size: 0.8rem;
      }
    }
  }
}

/* Email Subject Panel - Blue accent */
.email-subject-panel {
  border-color: #b8daff;
  background: linear-gradient(135deg, #f0f7ff 0%, #fff 100%);
  min-width: 220px;

  .panel-header {
    border-bottom-color: #b8daff;

    .panel-title {
      color: #0056b3;
    }
  }

  .required-star {
    color: #dc3545;
    font-weight: bold;
  }

  .email-subject-input {
    font-size: 0.85rem;
  }
}

.email-subject-error {
  border-color: #dc3545 !important;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%) !important;

  .panel-header {
    border-bottom-color: #dc3545 !important;
  }
}

/* Public Access Panel - Green accent */
.public-access-panel {
  border-color: #d4edda;
  background: linear-gradient(135deg, #f8fff9 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #d4edda;

    .panel-title {
      color: #28a745;
    }
  }

  .public-link-actions {
    margin-top: 6px;

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.75rem;
      padding: 3px 8px;
    }
  }
}

/* Translation Panel - Blue accent */
.translation-panel {
  border-color: #bee5eb;
  background: linear-gradient(135deg, #f0f9ff 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #bee5eb;

    .panel-title {
      color: #17a2b8;
    }
  }

  .auto-translate-info {
    font-size: 13px;
    color: #495057;
    display: flex;
    align-items: center;

    i {
      font-size: 14px;
    }
  }

  .translation-lang-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;

    .translation-checkbox {
      width: 15px;
      height: 15px;
      cursor: pointer;
      accent-color: #17a2b8;
    }

    .translation-label {
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      margin-bottom: 0;
      flex: 1;
    }

    .lang-flag {
      font-size: 16px;
    }

    .lang-name {
      color: #333;
    }

    .status-badge {
      font-size: 12px;
    }
  }
}

/* Page Content Section */
.content-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.content-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #2c3e50;
  border-radius: 12px 12px 0 0;
}

.content-section-icon {
  color: #3498db;
  font-size: 16px;
}

.content-section-title {
  font-size: 15px;
}

.content-section-hint {
  font-weight: 400;
  font-size: 13px;
  color: #94a3b8;
  margin-left: 12px;
}

/* Insert dropdown content */
.insert-dropdown-content {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: 400px;
  overflow-y: auto;
}

.insert-section-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 600;
  padding: 6px 12px 4px;
  margin: 0;
}

.variable-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.variable-chip {
  display: inline-block;
  padding: 3px 8px;
  font-size: 12px;
  color: #374151;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
}

.variable-chip:hover {
  background-color: #e0f2fe;
  border-color: #93c5fd;
}

/* Emoji inline grid inside dropdown */
.emoji-inline-grid {
  max-height: 200px;
  overflow-y: auto;
}

.emoji-category-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 8px 0 4px;
  font-weight: 600;
}

.emoji-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.emoji-item {
  cursor: pointer;
  font-size: 1.2rem;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1.4;
}

.emoji-item:hover {
  background-color: #f3f4f6;
}

.content-editor-container {
  padding: 16px;
  min-height: 300px;
}

/* Variables Panel - Flexible width, amber accent */
.variables-panel {
  flex: 2 1 280px;
  border-color: #ffeeba;
  background: linear-gradient(135deg, #fffdf5 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #ffeeba;

    .panel-title {
      color: #856404;
    }
  }

  .variables-content {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    .badge {
      font-size: 0.8rem;
      font-weight: 500;
      padding: 4px 8px;
      cursor: pointer;
    }
  }
}

/* Responsive - Stack on smaller screens */
@media (max-width: 768px) {
  .settings-row {
    flex-direction: column;
  }

  .settings-panel {
    flex: 1 1 100%;
  }
}

/* Spanish Translation Panel Styles (legacy - keeping for compatibility) */
.translation-settings-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  
  .translation-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
    
    .translation-icon {
      font-size: 1.2rem;
      margin-right: 8px;
    }
    
    .translation-title {
      font-weight: 600;
      color: #333;
      font-size: 0.95rem;
    }
  }
  
  .translation-content {
    .translation-info {
      color: #666;
      margin-bottom: 10px;
      line-height: 1.4;
    }
    
    .translation-status {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 5px;
      
      .badge {
        font-size: 0.75rem;
        font-weight: 500;
        padding: 4px 8px;
      }
    }
    
    .translation-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin-top: 10px;
      
      .btn {
        font-size: 0.8rem;
        padding: 4px 10px;
        
        i {
          margin-right: 4px;
        }
        
        &.btn-disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
}

/* Spanish Preview Modal Styles */
.spanish-preview-content {
  .preview-block {
    margin-bottom: 1rem;
    
    h3 {
      color: #333;
      font-size: 1.3rem;
      margin-bottom: 0.5rem;
    }
    
    p {
      color: #555;
      line-height: 1.6;
    }
    
    ul, ol {
      padding-left: 1.5rem;
      
      li {
        margin-bottom: 0.25rem;
      }
    }
  }
}

/* Phone Preview Styles */
.phone-preview-container {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  flex-wrap: wrap;
}

.phone-mockup {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.phone-frame {
  width: 280px;
  height: 500px;
  background: #1a1a1a;
  border-radius: 40px;
  padding: 12px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    inset 0 0 0 2px #333,
    inset 0 0 0 4px #1a1a1a;
  position: relative;
}

.phone-notch {
  width: 120px;
  height: 25px;
  background: #1a1a1a;
  border-radius: 0 0 20px 20px;
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  border-radius: 32px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.phone-header {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 35px 15px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
}

.phone-time {
  font-weight: 600;
}

.phone-carrier {
  font-size: 11px;
  opacity: 0.9;
}

.phone-content {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background: #e5ddd5;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.message-thread {
  display: flex;
  flex-direction: column;
}

.message-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 18px;
  margin-bottom: 8px;
  position: relative;
  word-wrap: break-word;
}

.message-bubble.outgoing {
  background: linear-gradient(135deg, #dcf8c6 0%, #c5e8a5 100%);
  align-self: flex-end;
  border-bottom-right-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-bubble.incoming {
  background: white;
  align-self: flex-start;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-text {
  font-size: 14px;
  line-height: 1.4;
  color: #303030;
}

.message-time {
  font-size: 10px;
  color: #8a8a8a;
  text-align: right;
  margin-top: 4px;
}

.phone-label {
  margin-top: 15px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.preview-note {
  text-align: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  
  code {
    background: #e9ecef;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.85em;
  }
}

/* Responsive phone preview */
@media (max-width: 768px) {
  .phone-preview-container {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
  
  .phone-frame {
    width: 240px;
    height: 420px;
  }
}

/* ================================= */
/* INLINE SMS PREVIEW SECTION STYLES */
/* ================================= */
/* SMS status card — the single counter + warn/block guidance in a clean, high-contrast card,
   deliberately OFF the preview panel's purple gradient (readability). Mirrors WikiCreate. */
.sms-status-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
}

.sms-preview-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
  position: relative;
  z-index: 0;
}

.preview-header {
  cursor: pointer;
  user-select: none;
  padding: 16px 20px;
  background: rgba(255,255,255,0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255,255,255,0.15);
  }
}

.preview-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  
  i.fa-chevron-down,
  i.fa-chevron-right {
    font-size: 12px;
    opacity: 0.8;
  }
  
  .title-icon {
    font-size: 20px;
  }
}

.preview-status {
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    
    i {
      font-size: 11px;
    }
  }
  
  .status-pending {
    background: rgba(255,255,255,0.2);
    color: white;
  }
  
  .status-ready {
    background: #28a745;
    color: white;
  }
  
  .status-edited {
    background: #17a2b8;
    color: white;
  }
  
  .status-warning {
    background: #ffc107;
    color: #333;
  }
  
  .status-english-only {
    background: rgba(255,255,255,0.25);
    color: white;
  }
}

.preview-loading {
  padding: 40px 20px;
  text-align: center;
  
  .loading-content {
    color: white;
    
    i {
      color: white;
      margin-bottom: 15px;
    }
    
    p {
      margin: 0;
      opacity: 0.9;
    }
  }
}

.inline-phone-preview {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 30px 20px;
  flex-wrap: wrap;
  
  &.single-phone {
    // Center single phone nicely
    .phone-mockup-inline {
      transform: scale(1.05);
    }
  }
}

.phone-mockup-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.phone-frame-inline {
  width: 220px;
  height: 380px;
  background: #1a1a1a;
  border-radius: 32px;
  padding: 10px;
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    inset 0 0 0 2px #333,
    inset 0 0 0 4px #1a1a1a;
  position: relative;
}

.phone-notch-inline {
  width: 90px;
  height: 20px;
  background: #1a1a1a;
  border-radius: 0 0 16px 16px;
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.phone-screen-inline {
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.phone-header-inline {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 28px 12px 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
}

.phone-time-inline {
  font-weight: 600;
}

.phone-carrier-inline {
  font-size: 10px;
  opacity: 0.9;
}

.phone-content-inline {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  background: #e5ddd5;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.message-bubble-inline {
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 14px;
  margin-bottom: 6px;
  position: relative;
  word-wrap: break-word;
  
  &.outgoing {
    background: linear-gradient(135deg, #dcf8c6 0%, #c5e8a5 100%);
    margin-left: auto;
    border-bottom-right-radius: 4px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
}

.message-text-inline {
  font-size: 12px;
  line-height: 1.35;
  color: #303030;
}

.message-time-inline {
  font-size: 9px;
  color: #8a8a8a;
  text-align: right;
  margin-top: 3px;
}

.phone-label-inline {
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.preview-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  background: rgba(0,0,0,0.15);
  flex-wrap: wrap;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  i {
    font-size: 12px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-edit {
  background: white;
  color: #667eea;
  
  &:hover:not(:disabled) {
    background: #f0f0f0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
}

.btn-refresh {
  background: rgba(255,255,255,0.2);
  color: white;
  
  &:hover:not(:disabled) {
    background: rgba(255,255,255,0.3);
    transform: translateY(-2px);
  }
}

/* SMS Stats Panel - Industry Standard UI */
.sms-stats-panel {
  padding: 16px 20px;
  background: rgba(255,255,255,0.98);
  border-radius: 0;

  &.counter-ok {
    border-left: 4px solid #28a745;
  }

  &.counter-caution {
    border-left: 4px solid #ffc107;
  }

  &.counter-split {
    border-left: 4px solid #667eea;
    background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  }

  &.counter-danger {
    border-left: 4px solid #dc3545;
    background: #fff8f8;
  }
}

/* Encoding Indicator */
.encoding-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.encoding-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;

  i {
    font-size: 11px;
  }

  &.gsm {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.unicode {
    background: #fff3e0;
    color: #e65100;
  }
}

.encoding-warning {
  font-size: 11px;
  color: #e65100;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 10px;
  }
}

/* Progress Bar */
.char-progress-container {
  margin-bottom: 14px;
}

.char-progress-bar {
  position: relative;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: visible;
}

.char-progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease, background 0.3s ease;

  &.progress-ok {
    background: linear-gradient(90deg, #28a745, #34ce57);
  }

  &.progress-caution {
    background: linear-gradient(90deg, #ffc107, #ffca2c);
  }

  &.progress-warning {
    background: linear-gradient(90deg, #667eea, #764ba2);
  }

  &.progress-danger {
    background: linear-gradient(90deg, #dc3545, #e4606d);
  }
}

.segment-markers {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.segment-marker {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: rgba(0,0,0,0.3);
  border-radius: 1px;

  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -3px;
    width: 8px;
    height: 8px;
    background: white;
    border: 2px solid rgba(0,0,0,0.3);
    border-radius: 50%;
  }
}

/* Stats Row */
.stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &.segments .stat-value {
    color: #667eea;
  }
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #343a40;
  line-height: 1.2;

  &.remaining {
    color: #28a745;

    &.low {
      color: #ffc107;
    }

    &.over {
      color: #dc3545;
    }
  }
}

.stat-label {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: #dee2e6;
}

/* Segment Cost Info */
.segment-cost-info {
  margin-top: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f0e6ff 0%, #e6f0ff 100%);
  border-radius: 6px;
  font-size: 12px;
  color: #5a4c8a;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #667eea;
  }
}

/* Split message bubble styles */
.message-bubble-inline.split-bubble {
  position: relative;
  margin-bottom: 10px;
  border: 2px dashed rgba(118, 75, 162, 0.3);
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
}

.split-indicator {
  position: absolute;
  top: -8px;
  right: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
}

.preview-footer {
  padding: 15px 20px;
  background: rgba(0,0,0,0.2);
  color: rgba(255,255,255,0.85);
  font-size: 13px;
  text-align: center;
  
  i {
    margin-right: 6px;
    opacity: 0.8;
  }
  
  code {
    background: rgba(255,255,255,0.2);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: white;
  }
}

/* ================================= */
/* EMAIL PREVIEW SECTION STYLES      */
/* ================================= */
.email-preview-section {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(79, 172, 254, 0.3);
}

.email-preview-header {
  cursor: pointer;
  user-select: none;
  padding: 16px 20px;
  background: rgba(255,255,255,0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255,255,255,0.15);
  }
}

.status-email {
  background: rgba(255,255,255,0.25);
  color: white;
}

.email-mockup {
  margin: 20px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
}

.email-client-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  padding: 12px 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #ddd;
}

.email-dots {
  display: flex;
  gap: 6px;
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    
    &.red { background: #ff5f56; }
    &.yellow { background: #ffbd2e; }
    &.green { background: #27ca40; }
  }
}

.email-tab {
  background: white;
  padding: 6px 14px;
  border-radius: 6px 6px 0 0;
  font-size: 12px;
  color: #666;
  border: 1px solid #ddd;
  border-bottom: none;
  margin-bottom: -1px;
  
  i {
    margin-right: 6px;
    color: #4facfe;
  }
}

.email-content {
  padding: 20px;
}

.email-field {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 13px;
  
  .field-label {
    font-weight: 600;
    color: #666;
    min-width: 60px;
  }
  
  .field-value {
    color: #333;
    flex: 1;
    
    &.subject {
      font-weight: 600;
      color: #222;
    }
  }
}

.email-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #ddd, transparent);
  margin: 15px 0;
}

.email-body {
  min-height: 100px;
  padding: 10px 0;
  font-size: 14px;
  line-height: 1.6;
  
  h1, h2, h3, h4, h5, h6 {
    color: #333;
    margin-top: 0;
  }
  
  p {
    margin-bottom: 12px;
  }
  
  ul, ol {
    padding-left: 20px;
  }
}

.email-preview-footer {
  padding: 15px 20px;
  background: rgba(0,0,0,0.15);
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  text-align: center;
  
  i {
    margin-right: 6px;
    opacity: 0.8;
  }
}

/* Responsive inline preview */
@media (max-width: 600px) {
  .inline-phone-preview {
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 15px;
  }
  
  .phone-frame-inline {
    width: 200px;
    height: 340px;
  }
  
  .preview-header-content {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Spanish Editor Modal Styles - Redesigned */
.spanish-editor-wrapper {
  min-height: 400px;
}

.spanish-editor-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.editor-panels {
  display: flex;
  gap: 0;
  flex: 1;
  min-height: 450px;
}

.editor-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.english-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-right: 2px solid #dee2e6;
}

.spanish-panel {
  background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  background: rgba(255,255,255,0.5);
}

.flag-icon {
  font-size: 20px;
}

.panel-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  flex: 1;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* English Content Styles */
.content-block {
  margin-bottom: 16px;
  padding: 12px 15px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.block-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.block-paragraph {
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 0;
}

.block-list {
  margin: 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  
  li {
    margin-bottom: 4px;
  }
}

/* Spanish Edit Styles */
.edit-block {
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border-left: 4px solid #ffc107;
}

.block-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 10px;
  
  i {
    color: #ffc107;
  }
}

.edit-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #ffc107;
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.15);
  }
  
  &.header-input {
    font-weight: 600;
    font-size: 15px;
  }
}

.list-editor {
  .list-item-edit {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    
    .list-number {
      font-weight: 600;
      color: #6c757d;
      min-width: 24px;
    }
    
    .edit-input {
      flex: 1;
      padding: 10px 12px;
      border: 2px solid #e9ecef;
      border-radius: 6px;
      font-size: 14px;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: #ffc107;
        box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.15);
      }
    }
  }
}

/* Footer Styles */
.editor-footer {
  padding: 15px 20px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6c757d;
  
  code {
    background: #e9ecef;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #e83e8c;
  }
}

.footer-actions {
  display: flex;
  gap: 10px;
  
  .btn {
    padding: 8px 20px;
    font-weight: 500;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .editor-panels {
    flex-direction: column;
  }
  
  .english-panel {
    border-right: none;
    border-bottom: 2px solid #dee2e6;
  }
  
  .editor-footer {
    flex-direction: column;
    align-items: stretch;
    
    .footer-actions {
      justify-content: flex-end;
    }
  }
}
</style>
